const TAG_PREFIX = "tx";
const MAX_KEY_LENGTH = 40;
const MAX_VALUE_LENGTH = 120;
const MAX_PENDING_OPERATIONS = 250;
const FLUSH_INTERVAL_MS = 1000;

let pendingOperations = [];
let flushTimerId = null;

const sanitizeKey = (key) => {
  const normalized = String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  return normalized.slice(0, MAX_KEY_LENGTH);
};

const sanitizeValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return "0";
    }

    return String(value);
  }

  return String(value).trim().slice(0, MAX_VALUE_LENGTH);
};

const sanitizePayload = (payload = {}) => {
  return Object.entries(payload).reduce((accumulator, [rawKey, rawValue]) => {
    const key = sanitizeKey(rawKey);
    if (!key) {
      return accumulator;
    }

    const value = sanitizeValue(rawValue);
    if (value === "") {
      return accumulator;
    }

    accumulator[key] = value;
    return accumulator;
  }, {});
};

const getClarityRuntime = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return typeof window.clarity === "function" ? window.clarity : null;
};

const stopFlushLoop = () => {
  if (typeof window === "undefined" || flushTimerId === null) {
    return;
  }

  window.clearInterval(flushTimerId);
  flushTimerId = null;
};

const flushPendingOperations = () => {
  const clarity = getClarityRuntime();
  if (!clarity || pendingOperations.length === 0) {
    return;
  }

  const operations = [...pendingOperations];
  pendingOperations = [];

  operations.forEach((operation) => {
    try {
      operation(clarity);
    } catch (error) {
      // Ignore non-critical analytics failures.
    }
  });

  if (pendingOperations.length === 0) {
    stopFlushLoop();
  }
};

const startFlushLoop = () => {
  if (typeof window === "undefined" || flushTimerId !== null) {
    return;
  }

  flushTimerId = window.setInterval(() => {
    flushPendingOperations();
  }, FLUSH_INTERVAL_MS);
};

const enqueueOperation = (operation) => {
  if (typeof window === "undefined") {
    return;
  }

  pendingOperations.push(operation);
  if (pendingOperations.length > MAX_PENDING_OPERATIONS) {
    pendingOperations.shift();
  }

  startFlushLoop();
};

const executeOrQueue = (operation) => {
  const clarity = getClarityRuntime();
  if (clarity) {
    operation(clarity);
    flushPendingOperations();
    return;
  }

  enqueueOperation(operation);
};

export const setAnalyticsTags = (payload = {}) => {
  const normalizedPayload = sanitizePayload(payload);
  executeOrQueue((clarity) => {
    Object.entries(normalizedPayload).forEach(([key, value]) => {
      clarity("set", `${TAG_PREFIX}_${key}`, value);
    });
  });
};

export const trackAnalyticsEvent = (eventName, payload = {}) => {
  const normalizedEvent = sanitizeKey(eventName);
  if (!normalizedEvent) {
    return;
  }

  const normalizedPayload = sanitizePayload(payload);
  executeOrQueue((clarity) => {
    Object.entries(normalizedPayload).forEach(([key, value]) => {
      clarity("set", `${TAG_PREFIX}_${key}`, value);
    });
    console.log("Sending Clarity event:", normalizedEvent);

    clarity("event", normalizedEvent);
  });
};

export const trackRouteEntered = ({
  pathname,
  previousPath = "",
  isEntry = false,
}) => {
  trackAnalyticsEvent("route_view", {
    route: pathname,
    previous_route: previousPath,
    is_entry: isEntry,
  });
};

export const trackRouteExited = ({
  pathname,
  nextPath = "",
  durationMs = 0,
  reason = "navigate",
}) => {
  const durationSeconds = Number((Math.max(0, durationMs) / 1000).toFixed(1));

  trackAnalyticsEvent("route_exit", {
    route: pathname,
    next_route: nextPath,
    duration_seconds: durationSeconds,
    reason,
  });
};

export const trackSessionStarted = ({ entryPath }) => {
  trackAnalyticsEvent("session_entry", {
    entry_page: entryPath,
  });
};

export const trackSessionEnded = ({ exitPath, totalDurationMs = 0 }) => {
  const durationSeconds = Number(
    (Math.max(0, totalDurationMs) / 1000).toFixed(1)
  );

  trackAnalyticsEvent("session_exit", {
    exit_page: exitPath,
    duration_seconds: durationSeconds,
    drop_off: 1,
  });
};
